/**
 * AchievementBadge Component
 *
 * Compact horizontal card for displaying an operator achievement.
 * Shows a colored icon circle + title + relative earned date.
 *
 * Icon/color mapping:
 *   first_assessment         -> ClipboardCheck (indigo)
 *   first_80_plus            -> Star (amber)
 *   five_day_streak          -> Flame (orange)
 *   all_training_complete    -> GraduationCap (violet)
 *   goal_achieved            -> Target (emerald)
 *   first_quiz_passed        -> Brain (cyan)
 *   first_personal_training  -> PlayCircle (guild-green) — Phase 25.7 Ring 1
 *   first_team_refresher     -> Users (sky) — Phase 25.7 Ring 2
 *
 * Designed for use inside a horizontal-scroll container.
 * Uses OperatorCard wrapper for consistent styling.
 */

import {
  ClipboardCheck,
  Star,
  Flame,
  GraduationCap,
  Target,
  Award,
  Brain,
  PlayCircle,
  Users,
} from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { OperatorCard } from './OperatorCard';
import type { Achievement } from '../hooks/useOperatorProgress';

interface AchievementBadgeProps {
  achievement: Achievement;
}

/** Badge icon + color config per achievement type */
function getBadgeConfig(type: string): {
  icon: typeof Award;
  bgColor: string;
  iconColor: string;
} {
  switch (type) {
    case 'first_assessment':
      return { icon: ClipboardCheck, bgColor: 'bg-indigo-100', iconColor: 'text-indigo-600' };
    case 'first_80_plus':
      return { icon: Star, bgColor: 'bg-amber-100', iconColor: 'text-amber-600' };
    case 'five_day_streak':
      return { icon: Flame, bgColor: 'bg-orange-100', iconColor: 'text-orange-600' };
    case 'all_training_complete':
      return { icon: GraduationCap, bgColor: 'bg-violet-100', iconColor: 'text-violet-600' };
    case 'goal_achieved':
      return { icon: Target, bgColor: 'bg-emerald-100', iconColor: 'text-emerald-600' };
    case 'first_quiz_passed':
      return { icon: Brain, bgColor: 'bg-cyan-100', iconColor: 'text-cyan-600' };
    case 'first_personal_training':
      // Phase 25.7 — first Ring 1 (`variant='targeted'`) completion.
      return { icon: PlayCircle, bgColor: 'bg-[#c5e8c2]', iconColor: 'text-[#00674a]' };
    case 'first_team_refresher':
      // Phase 25.7 — first Ring 2 (`variant` in refresher/focused_drill).
      return { icon: Users, bgColor: 'bg-sky-100', iconColor: 'text-sky-600' };
    default:
      return { icon: Award, bgColor: 'bg-gray-100', iconColor: 'text-gray-600' };
  }
}

export function AchievementBadge({ achievement }: AchievementBadgeProps) {
  const { icon: Icon, bgColor, iconColor } = getBadgeConfig(achievement.type);

  const earnedLabel = formatDistanceToNow(new Date(achievement.earnedAt), {
    addSuffix: true,
  });

  return (
    <OperatorCard className="flex-shrink-0 !p-3">
      <div className="flex items-center gap-3">
        {/* Icon circle */}
        <div
          className={`flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full ${bgColor} ${iconColor}`}
        >
          <Icon size={18} strokeWidth={1.5} />
        </div>

        {/* Text content */}
        <div className="min-w-0">
          <p className="text-sm font-semibold text-op-text-primary whitespace-nowrap">
            {achievement.title}
          </p>
          <p className="text-xs text-op-text-tertiary whitespace-nowrap">
            Earned {earnedLabel}
          </p>
        </div>
      </div>
    </OperatorCard>
  );
}
